import React, { useCallback, useEffect, useRef, useState } from 'react'

import NoImageIcon from 'components/icons/NoImageIcon'

import Spinner from './Spinner'

export enum ImageType {
  OBJECT_CONTAIN = 'object-contain',
  OBJECT_COVER = 'object-cover',
  OBJECT_FILL = 'object-fill'
}
type Props = {
  url: string
  name: string
  className?: string
  width?: number
  height?: number
  imageType?: ImageType
  onLoad?: () => void
}

export default function LazyThumbnail({
  url,
  name,
  className,
  width,
  height,
  imageType,
  onLoad
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null)

  const targetUrl = React.useMemo(() => {
    if (!url) return '/assets/no-image.svg'
    if (url.includes('/assets/no-image.svg')) {
      return '/assets/no-image.svg'
    }
    return url
  }, [url])

  const [imageUrl, setImageUrl] = useState(targetUrl)
  const [online, setOnline] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)

  const onlineListener = useCallback(() => setOnline(false), [setOnline])

  useEffect(() => {
    window.addEventListener('online', onlineListener)

    return () => {
      window.removeEventListener('online', onlineListener)
    }
  }, [onlineListener])

  useEffect(() => {
    if (!targetUrl || targetUrl === '/assets/no-image.svg') {
      setImageUrl(targetUrl)
      setIsLoaded(true)
      setOnline(false)
      onLoad?.()
      return
    }

    if (imageUrl === targetUrl) return

    // If initial load or previously showing placeholder, switch immediately
    if (!imageUrl || imageUrl === '/assets/no-image.svg') {
      setImageUrl(targetUrl)
      setIsLoaded(false)
      setOnline(false)
      return
    }

    // Image already displayed (updating from old to new thumbnail):
    // Preload the new image in background to avoid blinking or blanking out the existing thumbnail
    let isSubscribed = true
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = targetUrl

    img.onload = () => {
      if (isSubscribed) {
        setImageUrl(targetUrl)
        setIsLoaded(true)
        setOnline(false)
        onLoad?.()
      }
    }

    img.onerror = () => {
      if (isSubscribed) {
        setOnline(true)
        onLoad?.()
      }
    }

    return () => {
      isSubscribed = false
    }
  }, [targetUrl, imageUrl, onLoad])

  return (
    <div
      ref={containerRef}
      className={`flex relative ${className} z-0 bg-[#CED0D5]`}
    >
      {!url ? <Spinner /> : null}
      {online ? (
        <div className="m-auto">
          <NoImageIcon width={width} height={height} />
        </div>
      ) : (
        <img
          crossOrigin="anonymous"
          className={`w-full h-full transition-all duration-700 ease-in-out ${
            imageType || imageType === ImageType.OBJECT_CONTAIN
              ? imageType
              : 'object-contain'
          } select-none ${imageUrl ? '' : 'invisible'} ${
            isLoaded
              ? 'opacity-100 blur-xs scale-100'
              : 'opacity-40 blur-sm scale-100'
          }`}
          loading="lazy"
          onLoad={() => {
            setIsLoaded(true)
            onLoad?.()
          }}
          onError={() => {
            if (imageUrl) {
              setOnline(true)
              onLoad?.()
            }
          }}
          src={imageUrl}
          alt={name}
          draggable={false}
        />
      )}
    </div>
  )
}

LazyThumbnail.defaultProps = {
  className: 'w-full aspect-[2/1] max-w-[220px]',
  imageType: ImageType.OBJECT_CONTAIN,
  width: 220,
  height: 110,
  onLoad: () => {}
}
